<template>
  <div id="mealPlans">
    <div id="mealPlan" v-if="mealPlan" class="d-flex gap10 flex-column">
      <div v-for="group in mealPlan.groups" :key="group.description" class="meal-container">
        <div class="meal-group">
          <h2 class="meal-group-title py-1" style="font-size:20px;">
            <span class="text-left">{{ group.description }}</span>
            <span class="text-right">
              <strong>{{ productCountByGroup(group.id) }}</strong> / <strong>{{ group.maxProducts }}</strong>
              <span v-if="group.minProducts > 0" class="mealplan-group-requirement"> ({{ $t('text.minProducts') }}: {{ group.minProducts }})</span>
            </span>
          </h2>
        </div>
        <div class="grid-layout">
          <div
              v-for="product in group.items"
              :key="product?.kkProdId"
              :class="{ 'disabled-item': !isMealPlanValid || product.locked }"
          >
            <div
              :id="product.kkProdId"
              class="meal-item"
              @click="onMealItemClick($event, product, group)"
            >
              <img v-if="product.image1" :src="product.image1" :alt="product.name" class="meal-image" />
              <div class="d-flex flex-column gap10 flex-grow-1 justify-content-between">
                <div class="meal-details">
                  <div class="meal-description">
                    <span>{{ product.name }}</span>
                    <div class="properties"></div>
                    <div class="allergens">
                      <div v-for="property in product.properties" :key="property.id">
                        <div>
                          <img v-if="property.image" :src="property.image" :alt="property.name" class="property-img" />
                        </div>
                      </div>
                      <div v-for="allergen in product.allergens" :key="allergen.id">
                        <div>
                          <img v-if="allergen.image" :src="allergen.image" :alt="allergen.name" class="allergen-img" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-row gap10 w-100 flex-wrap">
                  <Button @click.stop="viewProductDetails(product.kkProdId, product.isLocked, product.name, group.id)"
                          icon="circle-info"
                          icon-only
                          preset-size
                          class="p-2 px-3"
                          :disabled="!isMealPlanValid"
                          aria-label="View product details"
                  />
                  <SpinButton
                    @click.stop
                    :min="0"
                    :max="getMaxAvailableForProduct(group.id, product.kkProdId)"
                    :modelValue="selectedProductsCount[group.id]?.[product.kkProdId] || 0"
                    @update:modelValue="({ value, type }) => handleProductUpdate(product.kkProdId, product.isLocked, product.hasChildren, product.name, type, group.id)"
                    :hide-decrement="product.hasChildren"
                    role="button"
                    tabindex="0"
                    aria-label="Add or remove product from basket"
                    @edit="openBasketProductEditor(product.kkProdId, product.name, group.id, product.isLocked)"
                  />
                  <span class="meal-price" style="white-space: nowrap">{{ product.formattedPrice }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal for product details -->
      <b-modal id="product-details-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
        <ProductDetails
          v-if="selectedProduct && selectedProduct.groupId"
          :productId="selectedProduct.productId"
          :max-quantity="getGroupRemainingCount(selectedProduct.groupId)"
          :groupId="selectedProduct.groupId"
          @close="closeProductDetailsModal"
          @product-update="handleProductUpdateWithOptions"
        />
      </b-modal>

      <!-- Modal for basket products -->
      <b-modal id="basket-product-editor-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
        <BasketProductEditor v-if="selectedProduct && selectedProduct.groupId"
           :basket-items="getBasketItemsByProdId(selectedProduct.productId)"
           :meal-plan-groups="mealPlan?.groups"
           :product-max-map="productMaxMap"
           @close="closeBasketProductEditorModal"
           @product-quantity-change="onProductQuantityChange"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import ProductDetails from "@/components/sub-components/Product/ProductDetails.vue";
import {basketService} from "@/services/api/basketService";
import SpinButton from "@/components/page-components/templates/SpinButton.vue";
import {OrderTypes} from "@/constants/constants";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import {encode64} from "@/utils/encodeUtils";
import Button from "@/components/barrier-free-template-components/Button.vue";
import { isMealPlanValid as isMealPlanValidUtil, getGroupProductCounts } from "@/utils/mealPlanUtils";
import BasketProductEditor from "@/components/sub-components/Basket/BasketProductEditor.vue";
import productDetailsMixin from "@/components/mixins/productDetailsMixin";

export default {
  name: "MealPlan",
  mixins: [productDetailsMixin],
  components: {
    BasketProductEditor,
    Button,
    SpinButton,
    ProductDetails,
  },
  props: {
    mealPlan: {
      type: Object,
      required: true,
    },
    basketTile: {
      type: Object,
      required: false,
      default: null
    },
    groupProductCount: {
      type: Object,
      required: false,
      default: () => ({})
    },
    productMaxMap: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedProduct: {},
      selectedProductsCount: {}, // { [groupId]: { [productId]: quantity } }
    };
  },
  computed: {
    isMealPlanValid() {
      return isMealPlanValidUtil(this.mealPlan);
    },
    /** @file public/settings/config/default.js */
    config() {
      return window.config;
    }
  },
  setup() {
    const appStore = useAppStore();
    const shopStore =useShopStore()

    return {
      appStore,
      shopStore
    };
  },
  watch: {
    basketTile: {
      immediate: true,
      handler(newBasketTile) {
        // Reset selectedProductsCount
        const counts = {};
        if (newBasketTile && Array.isArray(newBasketTile.basketItems)) {
          newBasketTile.basketItems.forEach(item => {
            let groupId = null;
            try {
              const ext = typeof item.extendedInfos === 'string' ? JSON.parse(item.extendedInfos) : item.extendedInfos;
              groupId = ext?.mealPlan?.groupId;
            } catch {
              groupId = item.groupId;
            }
            if (!groupId) return;
            if (!counts[groupId]) counts[groupId] = {};
            // Sum quantities for all items with the same prodId (regardless of options)
            if (!counts[groupId][item.prodId]) counts[groupId][item.prodId] = 0;
            counts[groupId][item.prodId] += Number(item.quantity) || 0;
          });
        }
        this.selectedProductsCount = counts;
      }
    }
  },
  methods: {
    async handleProductUpdateWithOptions(productId, quantity, price, productOptions, groupId) {
      await this.handleProductUpdate(productId, false, false, '', 'increment', groupId, productOptions, quantity);
    },
    async handleProductUpdate(productId, isLocked, hasChildren, name, type, groupId, productOptions, quantity = 1) {
      if (hasChildren) {
        if (type === 'increment') {
          this.viewProductDetails(productId, isLocked, name, groupId);
        }
      } else {
        if (type === 'increment') {
          await this.addToBasket(productId, quantity, productOptions, groupId);
        } else if (type === 'decrement') {
          await this.removeFromBasket(productId, quantity, groupId);
        }
      }
    },
    productCountByGroup(groupId) {
      // Use utility for group counts
      return getGroupProductCounts(this.basketTile?.basketItems)[groupId] || 0;
    },
    /**
     *
     * @param productId
     * @param quantity
     * @param {string[]} productOptions format: `["key=value","key1=value1","keyN=valueN"]`
     * @param groupId
     * */
    async addToBasket(productId, quantity, productOptions, groupId) {
      try {
        let result, options = null;
        if (Array.isArray(productOptions) && productOptions.length > 0) {
          // Defensive: flatten deeply nested arrays and filter only strings
          const flatOptions = [].concat(...productOptions).filter(opt => typeof opt === 'string');
          options = flatOptions.reduce((acc, option) => {
            const [key, value] = option.split('=');
            acc[key] = value;
            return acc;
          }, { prod: productId });
          options.kkjson = encode64(JSON.stringify(options));
        }

        const mealPlan = {
          mealPlanId: this.mealPlan.id,
          reservationDateTime: this.mealPlan.reservationDateTime,
          groupId: groupId
        };

        if (options) {
          result = await basketService.addComplexProductToBasket(productId, OrderTypes.DINE_IN, quantity, mealPlan, options);
        } else {
          result = await basketService.addProductToBasket(productId, OrderTypes.DINE_IN, quantity, mealPlan);
        }
        if (result.exception) {
          if (result.exception.code === 64) {
            this.makeToast('danger', this.$t('notification.addItemMinError'));
          } else if (result.exception.code === 65) {
            this.makeToast('danger', this.$t('notification.addItemMaxError'));
          } else {
            this.makeToast('danger', this.$t('notification.addItemFailed'));
          }
          return;
        }

        // Emit updated basketTile to parent, watcher will update counts and basket total
        this.$emit('basket-tile-updated', result.basketTile);

      } catch (error) {
        console.error("Error adding product to basket:", error);
        this.makeToast('danger', this.$t('notification.addItemFailed'));
      }
    },
    async removeFromBasket(productId, quantity) {
      try {
        const mealPlan = {
          mealPlanId: this.mealPlan.id,
          reservationDateTime: this.mealPlan.reservationDateTime
        };
        const result = await basketService.removeProductFromBasketByProductId(productId, quantity, OrderTypes.DINE_IN, mealPlan);
        if (result.exception) {
          const errorCode = result.exception.code;
          const errorMessages = {
            64: 'notification.removeItemMinError',
            65: 'notification.removeItemMaxError',
          };
          this.makeToast('danger', this.$t(errorMessages[errorCode] || 'notification.removeItemFailed'));
          return;
        }

        // Emit updated basketTile to parent, watcher will update counts and basket total
        this.$emit('basket-tile-updated', result.basketTile);

      } catch (error) {
        console.error("Error removing product from basket:", error);
        this.makeToast('danger', this.$t('notification.removeItemFailed'));
      }
    },
    // Get the current count of a product within a group
    getMaxAvailableForProduct(groupId, productId) {
      // Calculate the group max
      const group = this.mealPlan?.groups?.find(g => g.id === groupId);
      if (!group) return 0;
      const max = group.maxProducts || 0;
      // Calculate the total selected in this group (all products)
      const selectedInGroup = Object.values(this.selectedProductsCount[groupId] || {}).reduce((sum, val) => sum + val, 0);
      // Calculate the current selected for this product
      const currentSelected = this.selectedProductsCount[groupId]?.[productId] || 0;
      // The max you can set for this product is: max - (selectedInGroup - currentSelected)
      return Math.max(0, max - (selectedInGroup - currentSelected));
    },
    viewProductDetails(productId, isLocked, name, groupId) {
      if (this.isMealPlanValid && !isLocked) {
        this.selectedProduct = { productId, name, groupId };
        this.$bvModal.show("product-details-modal");
      }
    },
    onMealItemClick(event, product, group) {
      // Prevent if click originated from Button or SpinButton or their children
      const path = event.composedPath ? event.composedPath() : (event.path || []);
      if (
        path.some(
          el =>
            el?.classList?.contains('p-2') || // Button
            el?.classList?.contains('spin-button') // Add a class to SpinButton root if not present
        )
      ) {
        return;
      }
      this.viewProductDetails(product.kkProdId, product.isLocked, product.name, group.id);
    },
    getBasketItemsByProdId(prodId) {
      return this.basketTile?.basketItems?.filter(item => item.prodId === prodId) || [];
    },
    openBasketProductEditor(productId, name, groupId, isLocked) {
      if (this.isMealPlanValid && !isLocked) {
        this.selectedProduct = { productId, name, groupId };
        this.$bvModal.show("basket-product-editor-modal");
      }
    },
    closeBasketProductEditorModal() {
      this.$bvModal.hide("basket-product-editor-modal");
    },
    onProductQuantityChange(basketId, newQuantity) {
      this.$emit('product-quantity-change', basketId, newQuantity);
    },
    getGroupRemainingCount(groupId) {
      // Use the utility to get remaining count for a group
      const { getGroupRemainingCounts } = require('@/utils/mealPlanUtils');
      return getGroupRemainingCounts(this.basketTile?.basketItems, this.mealPlan?.groups)[groupId] || 0;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

/* Style updates for disabled state */
.button.disabled {
  background-color: #d3d3d3; /* Light gray to indicate disabled */
  pointer-events: none;
}

.disabled-item {
  opacity: 0.5;
  pointer-events: none;
}

.meal-container {
  background-color: var(--page-container-background);
  border-radius: var(--button-border-radius);
  margin-top: 4px
}

.meal-group {
  background: custom-chain(mealplan-group-color,var(--page-group-header));
  margin: 0 auto;
  padding: 0 10px;
  border-radius: var(--button-border-radius);
}

.meal-group-title {
  font-size: 16px;
  color: custom-chain(mealplan-group-color-text,var(--page-group-title));
  display: flex;
  justify-content: space-between; /* Space between the two spans */
  align-items: center; /* Center the text vertically */
  width: 100%; /* Ensure it takes the full width */
}

.mealplan-group-requirement {
  color: custom-chain(mealplan-group-color-requirement,red);
}

.text-left {
  flex: 1; /* Allow the left text to grow */
  text-align: left; /* Align left text */
}

.text-right {
  text-align: right; /* Align right text */
}

.meal-item {
  display: flex;
  flex: 1 0 360px;
  gap: 10px;
  text-align: left;
  align-items: stretch;
  padding: 10px;
  border-radius: var(--button-border-radius);
  background-color: var(--page-group-item);
  transition: background-color 0.3s ease, transform 0.3s ease; /* Smooth transition effect */
  height: 100%;
}

.meal-item:hover {
  background-color: #e0e0e0;
  transform: scale(1.02);
  cursor: pointer;
}

.meal-image {
  width: 120px;
  height: auto;
  border: custom-chain('mealplan-productimage-border','');
  border-radius: custom-chain('mealplan-productimage-border-radius','images-border-radius',var(--button-border-radius));
  object-fit: cover;
}

.meal-details {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.meal-description {
  font-weight: bold;
  text-align: left;
}

.properties {
  filter: hue-rotate(45deg);
}

.allergens, .properties {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 5px;
}

.allergen-img,
.property-img {
  max-width: 30px;
}

.meal-price {
  color: custom-chain(mealplan-meal-price-text,var(--background-text));
  font-size: 14px;
  align-self: center;
  flex-grow: 1;
  text-align: right;
}

.button {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.3s;
}

.button:hover,
.button:focus {
  background-color: #0056b3;
}

.button:active {
  background-color: #004494;
}
</style>



